import message from "@/utils/globalMessage";
import React from "react";
import { store } from '@redux/store';
import actions from '@redux/EditableTable/actions';
import TextArea from "antd/es/input/TextArea";
import moment from "moment";
import { Popover, Progress, Tooltip } from "antd";
import { InfoCircleOutlined, MessageOutlined, LoadingOutlined } from "@ant-design/icons";
import EquipmentLink from "@containers/facility/Equipment/EquipmentLink";
import EquipmentExpandLink from "@containers/facility/Equipment/EquipmentExpandLink";
import { getOrJoinEquipmentConversation } from "@components/workorder-chat/api";
import { emitEvent, EventName } from "@/hooks/useEventBus";

export const EquipmentProps = {
    title: 'Equipment',
    name: 'equipment',
    description: '',

}


export const EquipmentColumns = (handleSelectedRecord) => {
    /** hideInTable + hideInSetting：表格与「列展示」一致；否则勾选无效（表格仍会剔除 hideInTable 列）。 */
    const keySpecColumns = [1, 2, 3, 4, 5, 6, 7, 8].flatMap((n) => [
        {
            title: `Spec ${n} · Name`,
            dataIndex: `key_spec_${n}_name`,
            key: `key_spec_${n}_name`,
            editable: false,
            search: false,
            hideInTable: true,
            hideInSetting: true,
        },
        {
            title: `Spec ${n} · Value`,
            dataIndex: `key_spec_${n}_value`,
            key: `key_spec_${n}_value`,
            editable: false,
            search: false,
            hideInTable: true,
            hideInSetting: true,
        },
    ]);
    return ([
        // ========== 默认显示的列（按顺序）==========
        // Name
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            search: false,
            edit: true,
            fixed: 'left',
            width: 100,
            sorter: (a, b) => a.name?.localeCompare(b.name),
            formItemProps: {
                rules: [
                    {
                        required: true,
                        message: 'required',
                    },
                ],
            },
            render: (text, record, _, action) => {
                return (
                    <EquipmentLink id={record.id} text={text} action={action} />
                )
            },
            renderFormItem: () => <TextArea rows={1} />,
        },
        // Building
        {
            title: 'Building',
            dataIndex: 'building',
            key: 'building',
            edit: true,
            width: 100,
            sorter: (a, b) => a.building?.localeCompare(b.building),
        },
        // Area Served (原 Service)
        {
            title: 'Area Served',
            dataIndex: 'service',
            key: 'service',
            width: 150,
            edit: true,
            sorter: (a, b) => a.service?.localeCompare(b.service),
        },
        // Condition
        {
            title: 'Condition',
            dataIndex: 'condition',
            key: 'condition',
            filters: true,
            onFilter: true,
            edit: true,
            width: 100,
            align: 'left',
            sorter: (a, b) => a.condition?.localeCompare(b.condition),
            valueEnum: {
                Excellent: { text: 'Excellent' },
                Good: { text: 'Good' },
                Fair: { text: 'Fair' },
                Poor: { text: 'Poor' },
                Failed: { text: 'Failed' },
                Null: { text: 'Null' }
            },
            render: (text, record) => {
                return <span>{text || 'Null'}</span>
            },
            formItemProps: {
                initialValue: 'Null',
                rules: [
                    {
                        required: true,
                        message: 'Condition is required',
                    },
                ],
            },
        },
        // Category
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            editable: false,
            search: false,
        },
        // Modified Date
        {
            title: 'Modified Date',
            dataIndex: 'modified_date',
            key: 'modified_date',
            width: 100,
            editable: false,
            importIngore: true,
            align: 'right',
            sorter: (a, b) => a.modified_date - b.modified_date,
            render: (text, record) => (
                moment(record.modified_date).format('YYYY-MM-DD')
            )
        },
        // Equipment Score
        {
            title: 'Equipment Score',
            dataIndex: 'equipment_score',
            key: 'equipment_score',
            width: 120,
            editable: false,
            importIngore: true,
            align: 'right',
            sorter: (a, b) => (a.equipment_score || 0) - (b.equipment_score || 0),
            render: (text, record) => {
                const score = record.equipment_score || 0;
                let color = 'green';
                if (score < 4) color = 'red';
                else if (score < 7) color = 'orange';
                return <span style={{ color, fontWeight: 'bold' }}>{score.toFixed(1)}</span>
            }
        },
        // Outstanding WO
        {
            title: 'Outstanding WO',
            dataIndex: 'outstanding_workers',
            key: 'outstanding_workers',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: false,
            width: '14rem',
            align: 'right',
            render: (text, record) => {
                return (
                    <span>
                        <Progress
                            percent={text * 10}
                            steps={5}
                            showInfo={false}
                            strokeColor={[
                                'orange',
                                'orange',
                                'orange',
                                'orange',
                                'orange',
                            ]}
                        /> {text} </span>
                )
            }
        },
        // 90 days WO
        {
            title: <Popover content={
                <div>
                    <span>Number of Workorder created in the last 90 days: Completed/Total</span>
                </div>
            } trigger="hover"
            >
                <span className={'flex align-center'}>
                    90 days WO
                    <InfoCircleOutlined style={{ color: 'orange' }} />
                </span>
            </Popover>,
            dataIndex: 'recent_workorders_count',
            key: 'recent_workorders_count',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: false,
            width: '12rem',
            align: 'right',
            render: (text, record) => {
                return (
                    <span>
                        <Progress
                            percent={record.recent_workorders_notcompleted_count / text * 100}
                            steps={5}
                            showInfo={false}
                            strokeColor={[
                                'green',
                                'green',
                                'green',
                                'green',
                                'green',
                            ]}
                        /> {record?.recent_workorders_notcompleted_count + '/' + text} </span>
                )
            }
        },

        // ========== 默认隐藏的列 ==========
        // key (id)
        {
            title: 'key',
            dataIndex: 'id',
            key: 'id',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: false,
            width: '5rem'
        },
        // Description
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 150,
            edit: true,
            required: true,
            search: false,
            hideInTable: false,
            render: text => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            ),
            renderFormItem: () => <TextArea rows={1} />,
        },
        // Location
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            edit: true,
            width: 150,
            render: text => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            ),
            sorter: (a, b) => a.location?.localeCompare(b.location),
        },
        ...keySpecColumns,
        // Install Date
        {
            title: 'Install Date',
            dataIndex: 'install_date',
            key: 'install_date',
            width: 100,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => a.install_date - b.install_date,
            render: (text, record) => {
                const date = moment(record.install_date);
                return date.isValid() ? date.format('YYYY-MM-DD') : '-'
            }
        },
        // Equipment Age Score
        {
            title: 'Equipment Age Score',
            dataIndex: 'equipment_age_score',
            key: 'equipment_age_score',
            width: 150,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => (a.equipment_age_score || 0) - (b.equipment_age_score || 0),
            render: (text, record) => {
                const score = record.equipment_age_score || 0;
                return <span>{score.toFixed(1)}</span>
            }
        },
        // Visual Inspection Score
        {
            title: 'Visual Inspection Score',
            dataIndex: 'visual_inspection_score',
            key: 'visual_inspection_score',
            width: 170,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => (a.visual_inspection_score || 0) - (b.visual_inspection_score || 0),
            render: (text, record) => {
                const score = record.visual_inspection_score || 0;
                return <span>{score.toFixed(1)}</span>
            }
        },
        // Deficiency Score
        {
            title: 'Deficiency Score',
            dataIndex: 'deficiency_score',
            key: 'deficiency_score',
            width: 130,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => (a.deficiency_score || 0) - (b.deficiency_score || 0),
            render: (text, record) => {
                const score = record.deficiency_score || 0;
                return <span>{score.toFixed(1)}</span>
            }
        },
        // Score Updated Date
        {
            title: 'Score Updated Date',
            dataIndex: 'last_condition_update',
            key: 'last_condition_update',
            width: 150,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => {
                const dateA = a.last_condition_update ? new Date(a.last_condition_update) : new Date(0);
                const dateB = b.last_condition_update ? new Date(b.last_condition_update) : new Date(0);
                return dateA - dateB;
            },
            render: (text, record) => {
                return record.last_condition_update ? moment(record.last_condition_update).format('YYYY-MM-DD HH:mm') : '-'
            }
        },
        // Last Function Test
        {
            title: 'Last Function Test',
            dataIndex: 'recent_functionform',
            key: 'recent_functionform',
            width: 150,
            editable: false,
            importIngore: true,
            hideInTable: false,
            align: 'right',
            sorter: (a, b) => a.recent_functionform - b.recent_functionform,
            render: (text, record) => (
                record.recent_functionform ? moment(record.recent_functionform).format('YYYY-MM-DD') : ''
            )
        },
        // CreatedBy
        {
            title: 'CreatedBy',
            dataIndex: 'createdby_name',
            key: 'createdby_name',
            editable: false,
            search: false,
            hideInTable: false,
            importIngore: true
        },
        // created_date
        {
            title: 'created_date',
            dataIndex: 'created_date',
            key: 'created_date',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: true,
            hideInSetting: true,
        },
        // ModifiedBy
        {
            title: 'ModifiedBy',
            dataIndex: 'lastModified_name',
            key: 'lastModified_name',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: false,
        },
        // modified_date (隐藏字段)
        {
            title: 'modified_date',
            dataIndex: 'modified_date',
            key: 'modified_date_hidden',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: true,
            hideInSetting: true,
        },
        // Chat 列 - 打开设备会话
        {
            title: 'Chat',
            dataIndex: 'chat',
            key: 'chat',
            width: 60,
            fixed: 'right',
            editable: false,
            search: false,
            importIngore: true,
            hideInTable: false,
            align: 'center',
            render: (text, record) => {
                return <EquipmentChatButton record={record} />;
            }
        },
    ])
};

/**
 * 设备聊天按钮组件
 */
export const EquipmentChatButton = ({ record }) => {
    const [loading, setLoading] = React.useState(false);

    const handleOpenChat = async (e) => {
        e.stopPropagation();
        
        if (loading) return;
        
        setLoading(true);
    
        const member_ids = [...new Set([record.createdby, store.getState().Auth.userId])];

        try {
            const facilityId = store.getState().Facility.selected?.Id;
            const conversation = await getOrJoinEquipmentConversation(
                record.id,
                record.name,
                facilityId,
                member_ids
            );
            
            // 触发事件打开聊天面板并选中该会话
            emitEvent(EventName.OPEN_CHAT_WITH_CONVERSATION, { conversation });
        } catch (error) {
            console.error("Failed to open chat:", error);
            message.error("Failed to open chat");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Tooltip title="Open Chat">
            <span
                className="equipment-chat-btn"
                onClick={handleOpenChat}
                style={{
                    cursor: loading ? 'wait' : 'pointer',
                    color: '#1890ff',
                    fontSize: '18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {loading ? <LoadingOutlined /> : <MessageOutlined />}
            </span>
        </Tooltip>
    );
};
